import { useEffect, useMemo } from 'react';

import {
  API_CACHE_KEYS,
  ApiResponseCache,
  getTrainingExercisesCacheKey,
} from '../redux/api-response-cache';
import { useGetExerciseOptions } from '../redux/api/exerciseConfigAPI';
import {
  exerciseListNeedsOptionResolution,
  normalizeExerciseWithOptionRefs,
} from '../redux/api/exercises/map-available-exercises';
import { useGetAvailableExercises } from '../redux/api/exercisesAPI';
import { useGetTrainingExercisesQuery } from '../redux/api/trainingsAPI';
import { IExercise } from '../types/interfaces/exercise';
import { IExerciseOption } from '../types/interfaces/exercise-option';
import { orderBy, uniqBy } from '../utils/collections/array-utils';

export function useTrainingExercises(trainingId?: string) {
  const optionsState = useGetExerciseOptions();
  const resolvedExerciseOptions =
    optionsState.data ??
    ApiResponseCache.get<IExerciseOption[]>(API_CACHE_KEYS.EXERCISE_OPTIONS) ??
    (optionsState.isError ? [] : undefined);
  const optionsBlocking =
    resolvedExerciseOptions === undefined &&
    !optionsState.isError &&
    (optionsState.isLoading || optionsState.isFetching);

  const {
    data: availableToUserExercises,
    isLoading: isLoadingAvailableToUserExercises,
    isFetching: isFetchingAvailableToUserExercises,
    isError: isAvailableToUserExercisesError,
  } = useGetAvailableExercises();
  const {
    data: trainingExercisesFromQuery,
    isLoading: isLoadingTrainingExercisesQuery,
    isFetching: isFetchingTrainingExercisesQuery,
    isError: isTrainingExercisesError,
    error: trainingExercisesError,
  } = useGetTrainingExercisesQuery(trainingId, {
    skip: !trainingId,
  });
  const cachedTrainingExercises = useMemo(() => {
    if (!trainingId) {
      return undefined;
    }

    return (
      ApiResponseCache.get<IExercise[]>(
        getTrainingExercisesCacheKey(trainingId),
      ) ?? undefined
    );
  }, [trainingId]);
  const resolvedTrainingExercises =
    trainingExercisesFromQuery ?? cachedTrainingExercises;

  useEffect(() => {
    if (trainingId && trainingExercisesFromQuery) {
      ApiResponseCache.set(
        getTrainingExercisesCacheKey(trainingId),
        trainingExercisesFromQuery,
      );
    }
  }, [trainingId, trainingExercisesFromQuery]);

  useEffect(() => {
    const errorStatus = (
      trainingExercisesError as { status?: number | string } | undefined
    )?.status;

    if (
      trainingId &&
      isTrainingExercisesError &&
      errorStatus !== undefined &&
      errorStatus !== 'FETCH_ERROR'
    ) {
      ApiResponseCache.remove(getTrainingExercisesCacheKey(trainingId));
    }
  }, [trainingId, isTrainingExercisesError, trainingExercisesError]);

  const blockedByOptions =
    optionsBlocking &&
    (exerciseListNeedsOptionResolution(availableToUserExercises) ||
      exerciseListNeedsOptionResolution(resolvedTrainingExercises));

  const normalizedAvailableExercises = useMemo(() => {
    const list = availableToUserExercises ?? [];
    return list.map((exercise) =>
      normalizeExerciseWithOptionRefs(exercise, resolvedExerciseOptions ?? []),
    );
  }, [availableToUserExercises, resolvedExerciseOptions]);

  const normalizedTrainingExercises = useMemo(() => {
    const list = resolvedTrainingExercises ?? [];
    return list.map((exercise) =>
      normalizeExerciseWithOptionRefs(exercise, resolvedExerciseOptions ?? []),
    );
  }, [resolvedTrainingExercises, resolvedExerciseOptions]);

  const exercises = useMemo(() => {
    if (blockedByOptions) {
      return [];
    }

    const currentTrainingIds = new Set(
      normalizedTrainingExercises.map((item) => item.id),
    );
    const merged = uniqBy(
      [...normalizedAvailableExercises, ...normalizedTrainingExercises].map(
        (exercise) => ({
          ...exercise,
          isCurrentTrainingExercise: currentTrainingIds.has(exercise.id),
        }),
      ),
      'id',
    );

    return orderBy(merged, 'name');
  }, [
    blockedByOptions,
    normalizedAvailableExercises,
    normalizedTrainingExercises,
  ]);

  const hasAvailableExercisesData =
    !isLoadingAvailableToUserExercises && !isFetchingAvailableToUserExercises;
  const hasTrainingExercisesData =
    !trainingId ||
    resolvedTrainingExercises !== undefined ||
    (!isLoadingTrainingExercisesQuery && !isFetchingTrainingExercisesQuery);
  const shouldWaitForFirstPaint =
    !hasAvailableExercisesData && !hasTrainingExercisesData;

  const isLoadingTrainingExercises =
    !!trainingId &&
    (isLoadingTrainingExercisesQuery || isFetchingTrainingExercisesQuery);

  return {
    exercises,
    isLoading:
      blockedByOptions ||
      (shouldWaitForFirstPaint
        ? isLoadingAvailableToUserExercises ||
          isFetchingAvailableToUserExercises ||
          isLoadingTrainingExercises
        : isLoadingTrainingExercises),
    isError: isAvailableToUserExercisesError || isTrainingExercisesError,
  };
}
